import { STORE_NAMES, type ProfileId, type StoreName } from './entities';
import { MemoryRecordStore, type RecordStore } from './recordStore';

/**
 * IndexedDB backend.
 *
 * One database per profile. The name is derived from the profile and nothing
 * else, which is the mechanism behind private viewing isolation: a store
 * opened for the shared profile is connected to a different database file and
 * has no route to private records at all.
 */

const DB_VERSION = 1;

/** Database name for a profile. The whole isolation guarantee lives here. */
export function databaseName(profile: ProfileId): string {
  return `cinesync-${profile}`;
}

export class IndexedDbRecordStore implements RecordStore {
  readonly #db: IDBDatabase;

  private constructor(db: IDBDatabase) {
    this.#db = db;
  }

  static async open(profile: ProfileId): Promise<IndexedDbRecordStore> {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(databaseName(profile), DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        for (const name of STORE_NAMES) {
          if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Could not open the database'));
      request.onblocked = () =>
        reject(new Error('The database is blocked by another open window'));
    });

    return new IndexedDbRecordStore(db);
  }

  #run<T>(store: StoreName, mode: IDBTransactionMode, work: (s: IDBObjectStore) => IDBRequest<T>) {
    return new Promise<T>((resolve, reject) => {
      const tx = this.#db.transaction(store, mode);
      const request = work(tx.objectStore(store));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Storage request failed'));
      tx.onabort = () => reject(tx.error ?? new Error('Storage transaction aborted'));
    });
  }

  get(store: StoreName, id: string): Promise<unknown> {
    return this.#run(store, 'readonly', (s) => s.get(id));
  }

  async put(store: StoreName, id: string, value: unknown): Promise<void> {
    await this.#run(store, 'readwrite', (s) => s.put(value, id));
  }

  async delete(store: StoreName, id: string): Promise<void> {
    await this.#run(store, 'readwrite', (s) => s.delete(id));
  }

  async all(store: StoreName) {
    const [keys, values] = await Promise.all([
      this.#run<IDBValidKey[]>(store, 'readonly', (s) => s.getAllKeys()),
      this.#run<unknown[]>(store, 'readonly', (s) => s.getAll()),
    ]);
    return keys.map((key, index) => ({ id: String(key), value: values[index] }));
  }

  async clear(store: StoreName): Promise<void> {
    await this.#run(store, 'readwrite', (s) => s.clear());
  }

  close(): void {
    this.#db.close();
  }
}

/**
 * Open the best backend available.
 *
 * Some private browsing modes and locked-down profiles refuse IndexedDB
 * entirely. Falling back to memory keeps the application usable for the
 * session rather than refusing to start, and the caller is told which backend
 * it got so the interface can warn that nothing will be kept.
 */
export async function openRecordStore(
  profile: ProfileId,
): Promise<{ readonly store: RecordStore; readonly persistent: boolean }> {
  if (typeof indexedDB === 'undefined') {
    return { store: new MemoryRecordStore(), persistent: false };
  }
  try {
    return { store: await IndexedDbRecordStore.open(profile), persistent: true };
  } catch {
    return { store: new MemoryRecordStore(), persistent: false };
  }
}

/**
 * Destroy a profile's database entirely.
 *
 * Used by "erase private viewing data". Deleting the whole database rather
 * than clearing each store leaves nothing behind, not even the store
 * structure, which matters when the point is that no trace remains.
 */
export async function destroyProfileDatabase(profile: ProfileId): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(databaseName(profile));
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('Could not erase the database'));
    // A blocked delete still succeeds once other connections close, so this
    // resolves rather than failing and leaving the caller thinking it worked.
    request.onblocked = () => resolve();
  });
}
